import { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  getMeAgreementsPending,
  getGetMeAgreementsPendingQueryKey,
  usePostMeAgreementsAccept,
} from '@/api/auth/self/self';
import { PendingAgreement } from '@/api/auth/missionImpactAuthAPI.schemas';
import { logger } from '@/services/logger';

interface AgreementContextType {
  pendingAgreements: PendingAgreement[];
  isLoading: boolean;
  isChecked: boolean;
  acceptAgreement: (documentType: string, version: string) => Promise<void>;
  refetch: () => Promise<void>;
}

const AgreementContext = createContext<AgreementContextType | undefined>(undefined);

/**
 * Provider component that manages agreement state
 * Fetches pending agreements and provides methods for acceptance
 */
export function AgreementProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const [pendingAgreements, setPendingAgreements] = useState<PendingAgreement[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isChecked, setIsChecked] = useState(false);

  const acceptAgreementMutation = usePostMeAgreementsAccept();

  /**
   * Fetch pending agreements from the API
   */
  const fetchPendingAgreements = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await getMeAgreementsPending();
      const agreements = response.data.pending ?? [];

      logger.info('Agreement check completed', {
        context: 'AgreementContext.fetchPendingAgreements',
        pendingAgreementsCount: agreements.length,
        pendingDocumentTypes: agreements.map((a) => a.documentType),
      });

      setPendingAgreements(agreements);
    } catch (err) {
      logger.warn('Agreement check failed - allowing access', {
        context: 'AgreementContext.fetchPendingAgreements',
        error: err instanceof Error ? err.message : String(err),
      });
      // If agreement check fails, don't block the user
      setPendingAgreements([]);
    } finally {
      setIsLoading(false);
      setIsChecked(true);
    }
  }, []);

  /**
   * Accept a specific agreement
   */
  const acceptAgreement = useCallback(
    async (documentType: string, version: string) => {
      await acceptAgreementMutation.mutateAsync({
        data: { documentType: documentType as 'privacy-policy' | 'terms-of-use', version },
      });

      // Update local state to remove the accepted agreement
      setPendingAgreements((prev) =>
        prev.filter((a) => !(a.documentType === documentType && a.version === version))
      );

      // Invalidate the query cache
      await queryClient.invalidateQueries({
        queryKey: getGetMeAgreementsPendingQueryKey(),
      });

      logger.info('Agreement accepted', {
        context: 'AgreementContext.acceptAgreement',
        documentType,
        version,
      });
    },
    [acceptAgreementMutation, queryClient]
  );

  /**
   * Refetch pending agreements
   */
  const refetch = useCallback(async () => {
    await fetchPendingAgreements();
  }, [fetchPendingAgreements]);

  // Fetch agreements on mount
  useEffect(() => {
    void fetchPendingAgreements();
  }, [fetchPendingAgreements]);

  return (
    <AgreementContext.Provider
      value={{
        pendingAgreements,
        isLoading,
        isChecked,
        acceptAgreement,
        refetch,
      }}
    >
      {children}
    </AgreementContext.Provider>
  );
}

/**
 * Hook to access agreement context
 * @throws Error if used outside of AgreementProvider
 */
export function useAgreement() {
  const context = useContext(AgreementContext);
  if (context === undefined) {
    // eslint-disable-next-line no-restricted-syntax
    throw new Error('useAgreement must be used within an AgreementProvider');
  }
  return context;
}
